import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import { DopeTextRenderer } from '../renderer/bitmapFont';

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function hit(list, id, x, y, w, h, onClick) {
  list.push({ id, x, y, w, h, onClick });
}

/**
 * Draw the ticket / lay-low confirmation sheet. Every value comes from the
 * session state or the server tariff; the dialog never prices anything itself.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{width: number, height: number}} screen
 * @param {Object} confirm
 * @param {Object} actions
 * @returns {Array<Object>}
 */
export function drawTicketConfirm(ctx, screen, confirm, actions) {
  const hits = [];
  const w = Math.min(440, screen.width - 16);
  const h = Math.min(300, screen.height - 16);
  const x = Math.floor((screen.width - w) / 2);
  const y = Math.floor((screen.height - h) / 2);
  const buying = confirm?.mode === 'buy';

  ctx.save();
  ctx.globalAlpha = 0.78;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, screen.width, screen.height);
  ctx.restore();

  DopeUI.Panel(ctx, x, y, w, h, buying ? 'CONFIRM // TICKET' : 'CONFIRM // LAY LOW');
  const innerX = x + 14;
  const maxChars = Math.max(12, Math.floor((w - 28) / 8));
  let cursorY = y + 30;

  if (buying) {
    text(ctx, `TICKET TO ${confirm?.destination || '--'}`.slice(0, maxChars), innerX, cursorY, TERMINAL.TEXT_BRIGHT);
    cursorY += 18;
    text(ctx, 'ONE DISTRICT. ONE DAY. THIS TICKET IS FINAL.', innerX, cursorY, TERMINAL.TEXT_DIM);
    cursorY += 22;
  } else {
    text(ctx, 'LAY LOW // NO TICKET', innerX, cursorY, TERMINAL.TEXT_BRIGHT);
    cursorY += 18;
    text(ctx, 'STAY PUT. POSITIONS PERSIST. NO CHARGE.', innerX, cursorY, TERMINAL.TEXT_DIM);
    cursorY += 22;
  }

  const validFor = confirm?.forDay == null ? '--' : `DAY ${confirm.forDay}`;
  const rows = buying
    ? [
        ['DESTINATION', confirm?.destination || '--'],
        ['RISK TIER', confirm?.riskTier || '--'],
        ['TARIFF', confirm?.tariff || '--'],
        ['CASH NOW', confirm?.cash || '--'],
        ['VALID FOR', validFor],
      ]
    : [
        ['CHOICE', 'LAY LOW'],
        ['COST', 'FREE'],
        ['TARIFF', 'NOT CHARGED'],
        ['CASH NOW', confirm?.cash || '--'],
        ['VALID FOR', validFor],
      ];

  rows.forEach(([label, value]) => {
    text(ctx, label, innerX, cursorY, TERMINAL.TEXT_DIM);
    text(ctx, String(value).slice(0, maxChars - 20), innerX + 160, cursorY, TERMINAL.TEXT_BRIGHT);
    cursorY += 17;
  });

  cursorY += 6;
  if (buying) {
    text(ctx, 'LAY LOW INSTEAD: FREE. NO TICKET. NO CHARGE.', innerX, cursorY, TERMINAL.TEXT);
    cursorY += 15;
    text(ctx, 'THE TARIFF IS CHARGED ONCE AND CANNOT BE UNDONE.', innerX, cursorY, TERMINAL.TEXT_DIM);
  } else {
    text(ctx, 'NO TICKET. NO CHARGE.', innerX, cursorY, TERMINAL.TEXT);
    cursorY += 15;
    text(ctx, 'YOU CAN STILL BUY A TICKET ON A LATER DAY.', innerX, cursorY, TERMINAL.TEXT_DIM);
  }

  const failure = confirm?.error || null;
  const message = failure
    ? String(failure)
    : confirm?.loading
      ? 'WORKING...'
      : buying
        ? 'REVIEW // CONFIRM TO ISSUE'
        : 'REVIEW // CONFIRM TO LAY LOW';
  ctx.fillStyle = failure ? TERMINAL.GREEN_DARKEST : TERMINAL.BLACK;
  ctx.fillRect(innerX, cursorY + 8, w - 28, 20);
  text(
    ctx,
    message.slice(0, maxChars),
    innerX + 6,
    cursorY + 11,
    failure ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM,
  );

  const touch = screen.width < 500;
  const actionH = touch ? 44 : 24;
  const actionY = y + h - actionH - 12;
  DopeUI.Button(ctx, innerX, actionY, 132, actionH, 'CANCEL', 'normal');
  const confirmLabel = buying ? 'CONFIRM BUY' : 'CONFIRM LAY LOW';
  const canConfirm = !confirm?.loading;
  DopeUI.Button(
    ctx,
    x + w - 164,
    actionY,
    136,
    actionH,
    confirm?.loading ? 'WORKING...' : confirmLabel,
    canConfirm ? 'hover' : 'disabled',
  );
  hit(hits, 'tickets-cancel', innerX, actionY, 132, actionH, actions.cancel);
  if (canConfirm) hit(hits, 'tickets-confirm', x + w - 164, actionY, 136, actionH, actions.confirm);

  return hits;
}

export default drawTicketConfirm;
